// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {EquipmentCostReport} from 'Ui/features/onequip/reports/cost-report';
import {EquipmentUptimeReport} from 'Ui/features/onequip/reports/uptime-report';
import {EquipmentLifetimeReport} from 'Ui/features/onequip/reports/lifetime-report';
import EquipmentReportFilters from 'Ui/features/onequip/reports/equipment-report-filters';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import Panel from 'Ui/components/panel/panel';
import {getFleetEquipment} from 'Services/membership-service';
import {
    COST_BY_EQUIPMENT_AREA,
    COST_BY_EQUIPMENT_MODEL,
    COST_BY_EQUIPMENT_TYPE,
    EQUIPMENT_COST,
    EQUIPMENT_UPTIME,
    INVENTORIES,
    LIFETIME,
    LIFETIME_REPORT_START_DATE,
    LIFETIME_REPORTS
} from 'Ui/components/graph/constants/graph-filters';
import {fetchEffectData} from 'Utils/react-utils';
import {clsx} from 'clsx';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import moment from 'moment';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {VIEW_FINANCIAL_DATA} from 'Common/constants/business-activities';
import {MAINTENANCE_MANAGER_PROGOLF, PRO_GOLF_LABOR_REPORTS_1} from 'Common/constants/isg-toggles';
import EquipmentCostsV2 from './equipment-costs-v2';

const LOADING_ICON_PROPS = {
    className: 'dashboard-loading-icon',
    size: '50px'
};

const EQUIPMENT_REPORT_VIEWS = {
    [EQUIPMENT_COST]: EquipmentCostReport,
    [EQUIPMENT_UPTIME]: EquipmentUptimeReport,
    [COST_BY_EQUIPMENT_AREA]: EquipmentLifetimeReport,
    [COST_BY_EQUIPMENT_MODEL]: EquipmentLifetimeReport,
    [COST_BY_EQUIPMENT_TYPE]: EquipmentLifetimeReport,
    [INVENTORIES]: EquipmentLifetimeReport
};

function resolvePrimarySelectorForLifetimeChange(isLifetimeSelected, primarySelector) {
    const isLifetimeReport = LIFETIME_REPORTS.includes(primarySelector);

    if (isLifetimeSelected) {
        return isLifetimeReport ? primarySelector : COST_BY_EQUIPMENT_AREA;
    }

    return isLifetimeReport ? EQUIPMENT_COST : primarySelector;
}

function canRenderMaintenanceManagerV2(isMMProGolfEnabled, membership, myJdPermissions) {
    return isMMProGolfEnabled && isAuthorized({
        myJdPermissions: VIEW_FINANCIAL_DATA
    }, {
        isMigrated: membership.isMigrated,
        myJdPermissions
    });
}

function initializeStateAndRefs() {
    const [allData, setAllData] = React.useState(() => {
        const startDate = moment().startOf('month');
        const endDate = moment().endOf('month');

        return {
            dateSelect: null,
            endDate: endDate.format('YYYYMMDD'),
            equipment: [],
            startDate: startDate.format('YYYYMMDD')
        };
    });
    const [dateRangeSelection, setDateRangeSelection] = React.useState(
        () => [moment().startOf('month').toDate(), moment().endOf('month').toDate()]
    );
    const [loading, setLoading] = React.useState({
        equipment: true,
        dateSelect: true,
        primarySelector: true,
        secondarySelector: false,
        tertiarySelector: false
    });
    const [primarySelector, setPrimarySelector] = React.useState(EQUIPMENT_COST);
    const [secondarySelector, setSecondarySelector] = React.useState(null);
    const [tertiarySelector, setTertiarySelector] = React.useState(null);
    const [timeScale, setTimeScale] = React.useState('month');

    return {
        allData,
        dateRangeSelection,
        loading,
        primarySelector,
        setAllData,
        setDateRangeSelection,
        setLoading,
        setPrimarySelector,
        setSecondarySelector,
        setTertiarySelector,
        setTimeScale,
        secondarySelector,
        tertiarySelector,
        timeScale
    };
}

function EquipmentReport(props) {
    const {
        isgToggles,
        membership,
        myJdPermissions,
        translations
    } = props;

    const isMMProGolfEnabled = isgToggles?.[MAINTENANCE_MANAGER_PROGOLF];

    const {
        allData,
        dateRangeSelection,
        loading,
        primarySelector,
        setAllData,
        setDateRangeSelection,
        setLoading,
        setPrimarySelector,
        setSecondarySelector,
        setTertiarySelector,
        setTimeScale,
        secondarySelector,
        tertiarySelector,
        timeScale
    } = initializeStateAndRefs();

    const [equipmentManagerData, setEquipmentManagerData] = React.useState([]);
    const {addToast} = useToast();
    const [viewIndex, setViewIndex] = React.useState(0);

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        setLoading((prevLoading) => ({
            ...prevLoading,
            equipment: true
        }));

        const equipmentResponses = isMMProGolfEnabled ?
            await Promise.all([
                getFleetEquipment(),
                getFleetEquipment(isMMProGolfEnabled)
            ]) :
            [await getFleetEquipment()];
        const [{equipment: equipmentData}, equipmentManagerResponse] = equipmentResponses;

        if (isMounted()) {
            setAllData((prevAllData) => ({
                ...prevAllData,
                equipment: equipmentData
            }));
            if (equipmentManagerResponse) {
                setEquipmentManagerData(equipmentManagerResponse.equipment);
            }
            setLoading((prevLoading) => ({
                ...prevLoading,
                equipment: false
            }));
        }
    }), [membership.membershipId]);

    const updateDateRange = React.useCallback((newDates) => {
        const [startValue, endValue] = newDates;
        const hasRange = Boolean(startValue) && Boolean(endValue);
        const resolvedDates = hasRange ?
            newDates :
            [moment().startOf('month').toDate(), moment().endOf('month').toDate()];
        const isLifetimeSelected = moment(resolvedDates[0]).isSame(LIFETIME_REPORT_START_DATE, 'day');
        const nextPrimarySelector = resolvePrimarySelectorForLifetimeChange(isLifetimeSelected, primarySelector);
        const primarySelectorChanged = nextPrimarySelector !== primarySelector;

        setTimeScale(isLifetimeSelected ? LIFETIME : 'month');
        setDateRangeSelection(resolvedDates);

        setLoading((prevLoading) => ({
            ...prevLoading,
            dateSelect: true,
            ...primarySelectorChanged ? {
                primarySelector: true
            } : {}
        }));

        setAllData((prevAllData) => ({
            ...prevAllData,
            endDate: moment(resolvedDates[1]).format('YYYYMMDD'),
            startDate: moment(resolvedDates[0]).format('YYYYMMDD')
        }));

        if (primarySelectorChanged) {
            setPrimarySelector(nextPrimarySelector);
            setSecondarySelector(null);
            setTertiarySelector(null);
        }
    }, [primarySelector, setAllData, setDateRangeSelection, setLoading, setPrimarySelector, setTimeScale]);

    const className = clsx('graph-panel-wrapper', {
        hidden: loading.dateSelect || loading.equipment || loading.primarySelector || loading.secondarySelector || loading.tertiarySelector
    });

    const ReportComponent = EQUIPMENT_REPORT_VIEWS[primarySelector];

    const versionButtons = [
        {
            name: translations.ONLINK_DEFAULT,
            onClickHandler() {
                setViewIndex(0);
            }
        },
        {
            name: translations.ONLINK_HISTORICAL,
            onClickHandler() {
                setViewIndex(1);
            }
        }
    ];

    if (canRenderMaintenanceManagerV2(isMMProGolfEnabled, membership, myJdPermissions) && viewIndex === 0) {
        return (
            <LoadingWrapper
                {...LOADING_ICON_PROPS}
                loading={Boolean(loading.equipment)}
            >
                <EquipmentCostsV2
                    equipment={equipmentManagerData}
                    versionButtonsConf={versionButtons}
                    versionButtonsIndex={viewIndex}
                />
            </LoadingWrapper>
        );
    }
    return (
        <LoadingWrapper
            {...LOADING_ICON_PROPS}
            alwaysRenderChildren={true}
            loading={loading.dateSelect || loading.equipment || loading.primarySelector || loading.secondarySelector || loading.tertiarySelector}
        >
            <Panel
                className={className}
                title='ONLINK_EQUIPMENT_REPORT'
                translations={translations}
            >
                <EquipmentReportFilters
                    dateRangeValue={dateRangeSelection}
                    fleetEquipment={allData.equipment}
                    isMaintenanceManagerV2Enabled={canRenderMaintenanceManagerV2(isMMProGolfEnabled, membership, myJdPermissions)}
                    isMigrated={membership.isMigrated}
                    primarySelector={primarySelector}
                    secondarySelector={secondarySelector}
                    tertiarySelector={tertiarySelector}
                    timeScale={timeScale}
                    translations={translations}
                    updateDateRange={updateDateRange}
                    updatePrimarySelector={(selected) => {
                        if (selected !== primarySelector) {
                            setLoading((prevLoading) => ({
                                ...prevLoading,
                                primarySelector: true
                            }));
                        }

                        setPrimarySelector(selected);
                    }}
                    updateSecondarySelector={(selected) => {
                        if (selected !== secondarySelector &&
                            (LIFETIME_REPORTS.includes(primarySelector) || primarySelector === EQUIPMENT_COST)) {
                            setLoading((prevLoading) => ({
                                ...prevLoading,
                                secondarySelector: true
                            }));
                        }

                        setSecondarySelector(selected);
                    }}
                    updateTertiarySelector={(selected) => {
                        if (selected !== tertiarySelector && primarySelector === EQUIPMENT_COST) {
                            setLoading((prevLoading) => ({
                                ...prevLoading,
                                tertiarySelector: true
                            }));
                        }

                        setTertiarySelector(selected);
                    }}
                    versionButtonsConf={versionButtons}
                    versionButtonsIndex={viewIndex}
                />
                {
                    ReportComponent && allData.equipment &&
                    <ReportComponent
                        addToast={addToast}
                        dateSelect={allData.dateSelect}
                        endDate={allData.endDate}
                        fleetEquipment={allData.equipment}
                        isExportPreviewEnabled={Boolean(isgToggles?.[PRO_GOLF_LABOR_REPORTS_1])}
                        loading={loading}
                        membership={membership}
                        primarySelector={primarySelector}
                        secondarySelector={secondarySelector}
                        setLoading={setLoading}
                        startDate={allData.startDate}
                        tertiarySelector={tertiarySelector}
                        translations={translations}
                    />
                }
            </Panel>
        </LoadingWrapper>
    );
}

EquipmentReport.propTypes = {
    isgToggles: PropTypes.object,
    membership: PropTypes.membership,
    myJdPermissions: PropTypes.myJdPermissions,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        isgToggles: state.account.isgToggles,
        membership: state.membership,
        myJdPermissions: state.account.myJdPermissions
    };
}

export default connect(mapStateToProps)(EquipmentReport);
